import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ILoadingOverlayAngularComp } from 'ag-grid-angular';
import { SkeletonModule } from 'primeng/skeleton';
import { SkeletonOverlayParams, SkeletonColumn } from './ag-grid-skeleton-overlay.types';

@Component({
  selector: 'app-ag-grid-skeleton-overlay',
  standalone: true,
  imports: [CommonModule, SkeletonModule],
  templateUrl: './ag-grid-skeleton-overlay.component.html',
  styleUrls: ['./ag-grid-skeleton-overlay.component.scss'],
})
export class AgGridSkeletonOverlayComponent implements ILoadingOverlayAngularComp {
  columns: SkeletonColumn[] = [];
  rows: any[] = [];
  rowHeight: number = 40; // Default row height

  agInit(params: SkeletonOverlayParams): void {
    // Generate columns configuration
    if (params.columns) {
      // Use manually provided columns
      this.columns = params.columns.filter(col => !col.hide);
    } else if (params.columnDefs) {
      // Auto-generate from AG Grid column definitions
      this.columns = this.generateColumnsFromDefs(params.columnDefs);
    } else {
      // Use default columns as fallback
      this.columns = this.getDefaultColumns();
    }

    // Get row height from params
    this.rowHeight = params.rowHeight ?? 40;

    // Generate rows array
    const rowCount = params.rowCount ?? 10;
    this.rows = new Array(rowCount).fill({});
  }

  /**
   * Generate skeleton columns from AG Grid column definitions
   */
  private generateColumnsFromDefs(columnDefs: any[]): SkeletonColumn[] {
    return columnDefs
      .filter(col => !col.hide)
      .map(col => ({
        flex: col.flex ?? (col.width ? undefined : 1),
        width: col.width,
        minWidth: col.minWidth ?? 100,
        skeletonWidth: this.getSkeletonWidthByField(col.field),
      }));
  }

  /**
   * Auto-determine skeleton width based on field name
   */
  private getSkeletonWidthByField(field?: string): string {
    if (!field) return '80%';

    const fieldLower = field.toLowerCase();

    // ID fields: shorter
    if (fieldLower.includes('id') || fieldLower.includes('code')) {
      return '70%';
    }

    // Date/time fields: medium
    if (fieldLower.includes('date') || fieldLower.includes('time') || fieldLower.includes('opened')) {
      return '75%';
    }

    // Name/Description fields: longer
    if (fieldLower.includes('name') || fieldLower.includes('desc') || fieldLower.includes('description')) {
      return '90%';
    }

    // Status fields: medium
    if (fieldLower.includes('status')) {
      return '80%';
    }

    // Default
    return '80%';
  }

  /**
   * Fallback default columns if no configuration provided
   */
  private getDefaultColumns(): SkeletonColumn[] {
    return new Array(5).fill({ flex: 1, skeletonWidth: '80%' });
  }

  /**
   * Get CSS styles for skeleton cell
   */
  getCellStyle(column: SkeletonColumn): any {
    const style: any = {};

    if (column.width) {
      style.flex = 'none';
      style.width = `${column.width}px`;
    } else if (column.flex) {
      style.flex = column.flex;
    }

    if (column.minWidth) {
      style.minWidth = `${column.minWidth}px`;
    }

    return style;
  }

  /**
   * Get CSS class for skeleton cell
   */
  getCellClass(column: SkeletonColumn): string {
    const classes: string[] = ['loading-cell'];

    if (column.width) {
      classes.push('fixed-width');
    }

    return classes.join(' ');
  }

  /**
   * Get CSS styles for skeleton row
   */
  getRowStyle(): any {
    return {
      minHeight: `${this.rowHeight}px`,
      height: `${this.rowHeight}px`,
    };
  }
}
